import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { FolderPlus } from "lucide-react";
import { ActionButton } from "../../ui/ActionButton.tsx";
import { CustomTextField } from "../../ui/CustomTextField.tsx";
import FileValidation from "./FileValidation.tsx";
import { useNewCollectionDropzone } from "../../../hooks/useFileDropzone.ts";

interface AlertConfig {
  open: boolean;
  type: "loading" | "success" | "error" | "confirm";
  message: string;
  title?: string;
  onConfirm?: () => Promise<void>;
}

interface CollectionCreatorProps {
  newCollectionName: string;
  setNewCollectionName: (name: string) => void;
  newCollectionFiles: File[];
  setNewCollectionFiles: (files: File[]) => void;
  collectionNameError: string;
  setCollectionNameError: (error: string) => void;
  fileNameError: string;
  setFileNameError: (error: string) => void;
  showInlineSuccessMessage: boolean;
  isDarkMode: boolean;
  isCreatingCollection: boolean;
  onCreateCollection: () => Promise<void>;
  onNewCollectionFileSelection: (files: File[]) => void;
  setAlertConfig: (config: AlertConfig) => void;
  truncateFileName: (fileName: string, maxLength?: number) => string;
}

const CollectionCreator = ({
  newCollectionName,
  setNewCollectionName,
  newCollectionFiles,
  setNewCollectionFiles,
  collectionNameError,
  setCollectionNameError,
  fileNameError,
  setFileNameError,
  showInlineSuccessMessage,
  isDarkMode,
  isCreatingCollection,
  onCreateCollection,
  onNewCollectionFileSelection,
  setAlertConfig,
  truncateFileName
}: CollectionCreatorProps) => {

  const [textConfig, setTextConfig] = useState<any>({});

  const fetchTextConfig = async () => {
    try {
      const response = await fetch("/text.json");
      const data = await response.json();
      setTextConfig(data);
    } catch (error) {
      console.error("Error loading text config:", error);
    }
  };

  useEffect(() => {
    fetchTextConfig();
  }, []);

  const {
    getRootProps: getNewCollectionRootProps,
    getInputProps: getNewCollectionInputProps,
    isDragActive: isNewCollectionDragActive,
  } = useNewCollectionDropzone({
    onFileSelect: onNewCollectionFileSelection,
    setAlertConfig,
  });

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const maxLength = 25;
    if (e.target.value.length <= maxLength) {
      setNewCollectionName(e.target.value);
      setCollectionNameError("");
    } else {
      setCollectionNameError(textConfig.COLLECTION_CREATOR_NAME_MAX_LENGTH_ERROR);
    }
  };

  const handleRemoveFile = (index: number) => {
    setNewCollectionFiles(newCollectionFiles.filter((_, i) => i !== index));
  };

  const isDisabled = 
    newCollectionName.trim() === "" ||
    newCollectionFiles.length === 0 ||
    isCreatingCollection ||
    !!collectionNameError ||
    newCollectionName.length > 25;

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        border: isDarkMode ? "1px solid #2D2D2D" : "1px solid #e5e7eb",
        borderRadius: 2,
        backgroundColor: isDarkMode ? "#27272a" : "#f9fafb",
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight="bold"
        color={isDarkMode ? "#fff" : "#374151"}
        mb={1}
      >
        {textConfig.COLLECTION_CREATOR_TITLE}
      </Typography>

      <Typography
        color={isDarkMode ? "white" : "black"}
        fontSize="14px"
        mb={2}
      >
        {textConfig.COLLECTION_CREATOR_DESCRIPTION}
      </Typography>

      <div
        {...getNewCollectionRootProps()}
        className={`border-dashed border-2 p-4 rounded-lg cursor-pointer transition-colors ${
          isDarkMode ? "border-gray-600" : "border-gray-300"
        } ${isNewCollectionDragActive ? "border-green-400 bg-green-50 dark:bg-green-900/20" : ""}`}
        style={{
          backgroundColor: isDarkMode ? "#1f1f1f" : "#f9fafb",
          marginBottom: "1rem",
        }}
      >
        <input {...getNewCollectionInputProps()} />
        {isNewCollectionDragActive ? (
          <p className="text-green-400 text-center">
            {textConfig.COLLECTION_CREATOR_DROP_FILES}
          </p>
        ) : (
          <p
            className={`text-center ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            {textConfig.COLLECTION_CREATOR_DRAG_OR_CLICK}
          </p>
        )}
      </div>

      <FileValidation
        files={newCollectionFiles}
        isDarkMode={isDarkMode}
        fileNameError={fileNameError}
        showInlineSuccessMessage={showInlineSuccessMessage}
        onRemoveFile={handleRemoveFile}
        truncateFileName={truncateFileName}
        title={textConfig.COLLECTION_CREATOR_SELECTED_FILES_TITLE}
      />

      <Box display="flex" flexDirection="column" width="100%">
        <Box display="flex" alignItems="center" gap={2} mt={1}>
          <CustomTextField
            placeholder={textConfig.COLLECTION_CREATOR_NAME_PLACEHOLDER}
            value={newCollectionName}
            onChange={handleNameChange}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderColor: collectionNameError ? "#FF3D3D" : undefined,
              },
            }}
            error={!!collectionNameError}
            inputProps={{
              maxLength: 25,
            }}
          />
          <ActionButton
            label={textConfig.COLLECTION_CREATOR_CREATE_BUTTON}
            onClick={onCreateCollection}
            Icon={<FolderPlus />}
            size="medium"
            disabled={isDisabled}
          />
        </Box>
        
        {collectionNameError && (
          <Typography color="#FF3D3D" fontSize="12px" mt={1}>
            {collectionNameError}
          </Typography>
        )}
      </Box>

      <Box
        display="flex"
        justifyContent="flex-end"
        width="100%"
        mt={0.5}
        mb={1}
      >
        <Typography
          fontSize="12px"
          color={
            newCollectionName.length >= 25
              ? "#FF3D3D"
              : isDarkMode
              ? "#9CA3AF"
              : "#6B7280"
          }
        >
          {textConfig.COLLECTION_CREATOR_CHARACTER_COUNT?.replace('{current}', newCollectionName.length.toString()).replace('{max}', '25')}
        </Typography>
      </Box>
    </Box>
  );
};

export default CollectionCreator;